import { Link, useSearchParams } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import z from 'zod';
import { useForm } from '../../../hooks/Form';
import { api } from '../../../utils/api';
import { RESET_EMAIL_STEPS, useEmailResetContext } from '../EmailResetPage';

const schema = z.object({
  email: z.string()
    .nonempty('Email is required')
    .email('Invalid email address')
});

function createInitialValues() {
  return {
    email: ''
  };
}

function createInitialErrors() {
  return {
    email: null
  };
}

export default function EmailResetStart() {
  // eslint-disable-next-line no-unused-vars
  const [_, setSearchParams] = useSearchParams();
  const { setStep } = useEmailResetContext();

  const { isLoading, setIsLoading, error, setError, errors, setErrors, setFieldError, values, setFieldValue } = useForm({
    initialValues: createInitialValues(),
    initialErrors: createInitialErrors()
  });

  /** @param {React.FormEvent<HTMLFormElement>} event */
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      setError(null);
      setErrors(createInitialErrors());

      setIsLoading(true);

      const result = schema.safeParse(values);

      if (!result.success) {
        result.error.issues.forEach(err => {
          setFieldError(err.path[0], err.message);
        });

        return;
      }

      await api.post(`/email_resets`, {
        email: result.data.email
      }, {
        validateStatus: (status) => status === 201,
      });

      setSearchParams({ ce: encodeURIComponent(result.data.email) });

      setStep(RESET_EMAIL_STEPS.VERIFY);

    } catch {
      setError('We didn\'t find an account with that email address.');

    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit} className='form'>
        <h1 className='form__title'>
          Reset your email
        </h1>
        <p className='form__subtitle'>
          Enter your email address and we'll send you a link to reset your email
        </p>
        <div className='form__field-group'>
          <div className='text-field text-field--white form__field'>
            <label htmlFor='email' className='text-field__label'>
              E-mail
            </label>
            <input
              id='email'
              name='email'
              type='text'
              placeholder='example@domain.com'
              value={values.email}
              required
              onChange={(e) => setFieldValue('email', e.target.value)}
              className='text-field__input'
            />
            {errors.email && (
              <p className='text-field__error'>
                {errors.email}
              </p>
            )}
          </div>
        </div>
        {error && (
          <p className='error error--black'>
            {error}
          </p>
        )}
        <div className='form__button-group'>
          <Link className='button button--white button--outline' to='/signup'>
            Create Account
          </Link>
          <button type='submit' className='button button--white button--fill'>
            {isLoading ? 'Loading...' : 'Send E-mail'}
          </button>
        </div>
      </form>
    </Fragment>
  );
}